import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Link } from "react-router-dom";

const POLL_MS = 8000;

/**
 * Floating chat widget for buyers to message a store's seller directly.
 * - Lives on the store page (lower-right).
 * - Uses simple polling (8s) when open. Reads/writes via /api/store/{id}/chat.
 * - For unauthenticated visitors, prompts login.
 */
export default function StoreChatWidget({ store }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const isOwnStore = user && store && (user.user_id === store.owner_id);

  const load = useCallback(async () => {
    if (!user || !store?.store_id || isOwnStore) return;
    try {
      const { data } = await api.get(`/store/${store.store_id}/chat`);
      setConv(data);
      // The GET clears buyer_unread server-side; sync local state.
      setUnread(0);
    } catch { /* ignore */ }
  }, [user, store?.store_id, isOwnStore]);

  // Initial unread badge poll (cheap), runs whether widget is open or closed.
  useEffect(() => {
    if (!user || !store?.store_id || isOwnStore) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/me/chat/unread");
        const t = (data.buyer_threads || []).find(t => t.store_id === store.store_id);
        if (alive) setUnread(t?.buyer_unread || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS * 2);
    return () => { alive = false; clearInterval(id); };
  }, [user, store?.store_id, isOwnStore]);

  // Active poll while open
  useEffect(() => {
    if (!open) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, load]);

  // Auto-scroll to latest msg
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conv?.messages?.length, open]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/store/${store.store_id}/chat`, { text: t });
      setText("");
      setConv((c) => c
        ? { ...c, messages: [...(c.messages || []), data.message] }
        : { messages: [data.message] });
    } catch (ex) {
      const detail = ex?.response?.data?.detail || "Failed to send";
      alert(typeof detail === "string" ? detail : "Failed to send");
    } finally { setSending(false); }
  };

  if (isOwnStore) return null; // Don't show the widget on your own store

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="store-chat-fab"
        className={`fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110 ${
          open ? "bg-[#3D2845] text-[#FAFAFA]" : "bg-[#D4FF00] text-black"
        }`}
        aria-label={open ? "Close chat" : "Chat with seller"}
      >
        {open ? <X size={22}/> : <MessageCircle size={22} strokeWidth={2.5}/>}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF007F] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="store-chat-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="store-chat-panel"
          className="fixed bottom-36 right-4 sm:bottom-24 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[380px] max-w-md card overflow-hidden flex flex-col shadow-2xl"
          style={{ height: "min(70vh, 540px)" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#3D2845] flex items-center gap-3 bg-[#0F0F11]">
            {store?.logo_url && <img src={store.logo_url} className="w-9 h-9 rounded-lg" alt=""/>}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{store?.name}</div>
              <div className="text-[10px] text-[#A1A1AA] uppercase tracking-widest font-bold">Direct chat</div>
            </div>
          </div>

          {/* Body */}
          {!user ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
              <Lock size={28} className="text-[#A1A1AA] mb-3"/>
              <div className="font-display font-bold uppercase text-lg mb-1">Sign in to chat</div>
              <div className="text-xs text-[#A1A1AA] mb-4">Log in to send {store?.name} a direct message and track replies.</div>
              <Link to="/login" className="btn-primary text-xs"
                data-testid="store-chat-login-link">Log in</Link>
            </div>
          ) : (
            <>
              <div ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#0F0817]"
                data-testid="store-chat-messages">
                {(!conv || !(conv.messages || []).length) ? (
                  <div className="h-full flex flex-col items-center justify-center text-center px-4">
                    <MessageCircle size={28} className="text-[#52525B] mb-2"/>
                    <div className="text-sm font-bold mb-1">Say hi to {store?.name}</div>
                    <div className="text-xs text-[#A1A1AA]">Ask about sizing, shipping, or anything else — usually replies within a few hours.</div>
                  </div>
                ) : (
                  (conv.messages || []).map((m) => (
                    <ChatBubble key={m.msg_id} m={m} mine={m.from_role === "buyer"}/>
                  ))
                )}
              </div>

              {/* Composer */}
              <form onSubmit={send} className="p-3 border-t border-[#3D2845] bg-[#0F0F11] flex gap-2"
                data-testid="store-chat-composer">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  maxLength={2000}
                  data-testid="store-chat-input"
                  className="input-dark flex-1 px-3 py-2 text-sm"/>
                <button type="submit" disabled={sending || !text.trim()}
                  data-testid="store-chat-send"
                  className="btn-primary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Send size={14}/>
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}

function ChatBubble({ m, mine }) {
  const time = new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
        mine ? "bg-[#D4FF00] text-black" : "bg-[#3D2845] text-[#FAFAFA]"
      }`}>
        <div className="whitespace-pre-line break-words">{m.text}</div>
        <div className={`text-[9px] mt-1 ${mine ? "text-black/60" : "text-[#A1A1AA]"} text-right`}>
          {time}
        </div>
      </div>
    </div>
  );
}
